import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip,
  CartesianGrid,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import PageHeader from '../../../components/layout/PageHeader';
import {
  Activity, AlertTriangle, ArrowDownRight, ArrowUpRight, Briefcase, Check,
  ExternalLink, FileText, Gauge, Lightbulb, Loader2, Newspaper, Play, Plus,
  RefreshCw, Radar, Wallet,
} from 'lucide-react';
import { classForVerdict, runHref } from '../../../lib/committeeHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function inr(n, opts = {}) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  return `₹${Number(n).toLocaleString('en-IN', {
    maximumFractionDigits: opts.decimals ?? 0,
  })}`;
}

function pct(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  const v = Number(n);
  return `${v > 0 ? '+' : ''}${v.toFixed(2)}%`;
}

const SEVERITY_CLASS = {
  info: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  warning: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  critical: 'bg-red-500/10 text-red-500 border-red-500/30',
};

const FACTOR_STATUS_CLASS = {
  green: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30',
  amber: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  red: 'bg-red-500/10 text-red-500 border-red-500/30',
};

const EVENT_LABEL = {
  stop_hit: 'Stop hit',
  target_hit: 'Target hit',
  price_move: 'Price move',
  news: 'News',
  factor_trip: 'Factor trip',
  verdict_change: 'Verdict change',
  suggestion: 'Suggestion',
  rerun: 'Re-run',
};

// ── Creation wizard ──────────────────────────────────────────

function CreateWizard({ onCreated }) {
  const [amount, setAmount] = useState('1000000');
  const [name, setName] = useState('Committee Portfolio');
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [covering, setCovering] = useState(false);
  const [coverMsg, setCoverMsg] = useState(null);

  const doPreview = useCallback(async () => {
    setBusy(true); setError(null); setPreview(null);
    try {
      const { data } = await axios.post(`${API}/committee/portfolio/preview`, {
        amount_inr: Number(amount),
      });
      if (data.error) setError(data.error);
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, [amount]);

  const doCreate = useCallback(async () => {
    setCreating(true); setError(null);
    try {
      const { data } = await axios.post(`${API}/committee/portfolio`, {
        amount_inr: Number(amount), name,
      });
      onCreated?.(data.portfolio_id);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setCreating(false);
    }
  }, [amount, name, onCreated]);

  const doCoverNifty = useCallback(async () => {
    setCovering(true); setCoverMsg(null);
    try {
      const { data } = await axios.post(`${API}/committee/portfolio/coverage/nifty50`);
      setCoverMsg(`Nifty 50 added to nightly committee coverage (${data.covered} names). Decisions build up after the next nightly sweep.`);
    } catch (e) {
      setCoverMsg(e?.response?.data?.detail || e.message);
    } finally {
      setCovering(false);
    }
  }, []);

  const positions = preview?.positions || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="w-4 h-4" /> Build a committee portfolio
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Enter how much you want to invest. The committee&apos;s latest verdicts across
          the Nifty 50 are assembled into a tracked portfolio: BUY/ADD names weighted by
          committee sizing and conviction, with stops and targets from each decision.
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-xs text-muted-foreground mb-1">Investment amount (₹)</div>
            <Input
              type="number" min="10000" step="10000" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-44"
            />
          </div>
          <div>
            <div className="text-xs text-muted-foreground mb-1">Portfolio name</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="w-56" />
          </div>
          <Button onClick={doPreview} disabled={busy || !Number(amount)}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Gauge className="w-4 h-4 mr-1" />}
            Preview allocation
          </Button>
          <Button variant="outline" onClick={doCoverNifty} disabled={covering}>
            {covering ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            Cover Nifty 50 nightly
          </Button>
        </div>
        {coverMsg ? <div className="text-xs text-muted-foreground">{coverMsg}</div> : null}
        {error ? (
          <div className="text-sm text-red-500 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
          </div>
        ) : null}

        {preview && positions.length > 0 ? (
          <div className="space-y-3">
            <div className="text-sm">
              {positions.length} positions from {preview.buy_candidates} BUY/ADD verdicts
              ({preview.decisions_found}/{preview.universe_size} Nifty 50 names covered).
              Residual cash: <span className="font-medium">{inr(preview.cash_inr)}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="py-2 pr-3">Stock</th>
                    <th className="py-2 pr-3">Verdict</th>
                    <th className="py-2 pr-3 text-right">Weight</th>
                    <th className="py-2 pr-3 text-right">Qty</th>
                    <th className="py-2 pr-3 text-right">Price</th>
                    <th className="py-2 pr-3 text-right">Cost</th>
                    <th className="py-2 pr-3 text-right">Stop</th>
                    <th className="py-2 pr-3 text-right">Target</th>
                    <th className="py-2">Thesis</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((p) => (
                    <tr key={p.isin} className="border-b border-border/50">
                      <td className="py-2 pr-3 font-medium whitespace-nowrap">{p.nse_symbol || p.isin}</td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className={classForVerdict(p.verdict)}>
                          {p.verdict} {p.conviction}/10
                        </Badge>
                      </td>
                      <td className="py-2 pr-3 text-right">{p.weight_pct}%</td>
                      <td className="py-2 pr-3 text-right">{p.qty}</td>
                      <td className="py-2 pr-3 text-right">{inr(p.price, { decimals: 2 })}</td>
                      <td className="py-2 pr-3 text-right">{inr(p.cost_inr)}</td>
                      <td className="py-2 pr-3 text-right">{p.stop_price_inr ? inr(p.stop_price_inr, { decimals: 2 }) : '—'}</td>
                      <td className="py-2 pr-3 text-right">{p.target_price_inr ? inr(p.target_price_inr, { decimals: 2 }) : '—'}</td>
                      <td className="py-2 text-xs text-muted-foreground max-w-[320px] truncate" title={p.one_line_thesis}>
                        {p.one_line_thesis}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button onClick={doCreate} disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
              Create portfolio &amp; start tracking
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ── Dashboard pieces ─────────────────────────────────────────

function SummaryCards({ detail }) {
  const pnlPositive = (detail.pnl_pct ?? 0) >= 0;
  const items = [
    { label: 'NAV', value: inr(detail.nav_inr), icon: Wallet },
    { label: 'Invested', value: inr(detail.invested_amount_inr), icon: Briefcase },
    {
      label: 'P&L',
      value: pct(detail.pnl_pct),
      icon: pnlPositive ? ArrowUpRight : ArrowDownRight,
      cls: pnlPositive ? 'text-emerald-500' : 'text-red-500',
    },
    { label: 'Cash', value: inr(detail.cash_inr), icon: Wallet },
    {
      label: 'Open positions',
      value: detail.holdings.filter((h) => h.status === 'open').length,
      icon: Activity,
    },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      {items.map((it) => (
        <Card key={it.label}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <it.icon className="w-3.5 h-3.5" /> {it.label}
            </div>
            <div className={`text-lg font-semibold mt-1 ${it.cls || ''}`}>{it.value}</div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function HoldingsTab({ detail }) {
  const holdings = detail.holdings || [];
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b">
            <th className="py-2 pr-3">Stock</th>
            <th className="py-2 pr-3 text-right">Qty</th>
            <th className="py-2 pr-3 text-right">Entry</th>
            <th className="py-2 pr-3 text-right">Last</th>
            <th className="py-2 pr-3 text-right">P&amp;L</th>
            <th className="py-2 pr-3 text-right">Value</th>
            <th className="py-2 pr-3 text-right">Stop</th>
            <th className="py-2 pr-3 text-right">Target</th>
            <th className="py-2 pr-3">Entry verdict</th>
            <th className="py-2 pr-3">Entry thesis</th>
            <th className="py-2 pr-3">Latest verdict</th>
            <th className="py-2 pr-3">Latest thesis</th>
            <th className="py-2"></th>
          </tr>
        </thead>
        <tbody>
          {holdings.map((h) => (
            <tr key={h.id} className={`border-b border-border/50 ${h.status === 'closed' ? 'opacity-50' : ''}`}>
              <td className="py-2 pr-3 font-medium whitespace-nowrap">
                {h.nse_symbol || h.isin}
                {h.status === 'closed' ? <span className="ml-1 text-xs text-muted-foreground">(closed)</span> : null}
              </td>
              <td className="py-2 pr-3 text-right">{h.qty}</td>
              <td className="py-2 pr-3 text-right">{inr(h.entry_price_inr, { decimals: 2 })}</td>
              <td className="py-2 pr-3 text-right">{h.last_price_inr ? inr(h.last_price_inr, { decimals: 2 }) : '—'}</td>
              <td className={`py-2 pr-3 text-right font-medium ${(h.pnl_pct ?? 0) >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                {pct(h.pnl_pct)}
              </td>
              <td className="py-2 pr-3 text-right">{inr(h.value_inr)}</td>
              <td className="py-2 pr-3 text-right">{h.stop_price_inr ? inr(h.stop_price_inr, { decimals: 2 }) : '—'}</td>
              <td className="py-2 pr-3 text-right">{h.target_price_inr ? inr(h.target_price_inr, { decimals: 2 }) : '—'}</td>
              <td className="py-2 pr-3">
                {h.entry_verdict ? (
                  h.entry_run_id ? (
                    <Link to={runHref(h.entry_run_id)}>
                      <Badge variant="outline" className={`${classForVerdict(h.entry_verdict)} cursor-pointer`}>
                        {h.entry_verdict} {h.entry_conviction}/10
                      </Badge>
                    </Link>
                  ) : (
                    <Badge variant="outline" className={classForVerdict(h.entry_verdict)}>
                      {h.entry_verdict} {h.entry_conviction}/10
                    </Badge>
                  )
                ) : '—'}
              </td>
              <td className="py-2 pr-3 text-xs text-muted-foreground max-w-[200px] truncate" title={h.one_line_thesis}>
                {h.one_line_thesis}
                {h.entry_run_id ? (
                  <Link to={runHref(h.entry_run_id)} className="block mt-1">
                    <span className="text-primary hover:underline text-[11px]">View entry thesis →</span>
                  </Link>
                ) : null}
              </td>
              <td className="py-2 pr-3">
                {h.latest_verdict ? (
                  h.latest_run_id ? (
                    <Link to={runHref(h.latest_run_id)}>
                      <Badge variant="outline" className={`${classForVerdict(h.latest_verdict)} cursor-pointer`}>
                        {h.latest_verdict} {h.latest_conviction}/10
                      </Badge>
                    </Link>
                  ) : (
                    <Badge variant="outline" className={classForVerdict(h.latest_verdict)}>
                      {h.latest_verdict} {h.latest_conviction}/10
                    </Badge>
                  )
                ) : '—'}
              </td>
              <td className="py-2 pr-3 text-xs text-muted-foreground max-w-[200px] truncate" title={h.latest_thesis}>
                {h.latest_thesis || h.one_line_thesis}
                {h.latest_run_id ? (
                  <Link to={runHref(h.latest_run_id)} className="block mt-1">
                    <span className="text-primary hover:underline text-[11px]">View latest thesis →</span>
                  </Link>
                ) : null}
              </td>
              <td className="py-2"></td>
            </tr>
          ))}
        </tbody>
      </table>
      {holdings.length === 0 ? (
        <div className="text-sm text-muted-foreground py-6 text-center">No positions.</div>
      ) : null}
    </div>
  );
}

function NewsTab({ portfolioId, holdings }) {
  const [items, setItems] = useState(null);
  // Client-side ISIN → symbol map so badges never show raw ISINs even when
  // the API response predates the `symbols` field (stale cache, old server).
  const symbolByIsin = useMemo(() => {
    const m = {};
    for (const h of holdings || []) m[h.isin] = h.nse_symbol || h.company_name || h.isin;
    return m;
  }, [holdings]);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/committee/portfolio/${portfolioId}/news`, { params: { hours: 168, limit: 80 } })
      .then(({ data }) => { if (alive) setItems(data); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, [portfolioId]);

  if (items === null) return <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!items.length) return <div className="text-sm text-muted-foreground py-6 text-center">No news matched to holdings in the last 7 days.</div>;
  return (
    <div className="space-y-2">
      {items.map((n, i) => (
        <div key={`${n.url}-${i}`} className="flex items-start gap-3 p-3 rounded-lg border border-border/60">
          <Badge variant="outline" className={n.materiality >= 4 ? SEVERITY_CLASS.critical : (n.materiality === 3 ? SEVERITY_CLASS.warning : SEVERITY_CLASS.info)}>
            M{n.materiality}
          </Badge>
          <div className="min-w-0 flex-1">
            <a href={n.url} target="_blank" rel="noreferrer" className="text-sm font-medium hover:underline flex items-center gap-1">
              {n.title} <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
            {n.summary ? <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.summary}</div> : null}
            <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-2">
              <span>{n.source}</span>
              {n.published_at ? <span>{new Date(n.published_at).toLocaleString()}</span> : null}
              {(n.symbols || (n.isins || []).map((i) => symbolByIsin[i] || i)).map((sym) => (
                <Badge key={sym} variant="outline" className="text-[10px]">{sym}</Badge>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FactorsTab({ portfolioId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/committee/portfolio/${portfolioId}/factors`)
      .then(({ data }) => { if (alive) setRows(data); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [portfolioId]);

  const rollup = useMemo(() => {
    if (!rows) return [];
    const m = new Map();
    for (const r of rows) {
      for (const f of r.model?.factors || []) {
        if (f.magnitude !== 'high') continue;
        const cur = m.get(f.slug) || { name: f.name, status: f.status, holdings: 0, weight: 0 };
        cur.holdings += 1;
        cur.weight += Number(r.weight_pct || 0);
        if (f.status === 'red' || (f.status === 'amber' && cur.status === 'green')) cur.status = f.status;
        m.set(f.slug, cur);
      }
    }
    return [...m.values()].filter((x) => x.holdings >= 2).sort((a, b) => b.weight - a.weight);
  }, [rows]);

  if (rows === null) return <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  return (
    <div className="space-y-4">
      {rollup.length > 0 ? (
        <Card>
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Radar className="w-4 h-4" /> Shared high-magnitude exposures</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {rollup.map((r) => (
              <Badge key={r.name} variant="outline" className={FACTOR_STATUS_CLASS[r.status] || FACTOR_STATUS_CLASS.green}>
                {r.name} · {r.holdings} holdings · {r.weight.toFixed(0)}% of book
              </Badge>
            ))}
          </CardContent>
        </Card>
      ) : null}
      {rows.map((r) => (
        <Card key={r.isin}>
          <CardHeader>
            <CardTitle className="text-sm flex items-center justify-between">
              <span>{r.nse_symbol || r.isin}{r.company_name ? ` — ${r.company_name}` : ''}</span>
              {r.model?.refreshed_at ? (
                <span className="text-xs font-normal text-muted-foreground">
                  researched {new Date(r.model.refreshed_at).toLocaleDateString()}
                </span>
              ) : null}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!r.model || !(r.model.factors || []).length ? (
              <div className="text-xs text-muted-foreground">
                Factor model not researched yet — discovery runs automatically after portfolio creation
                and on the next committee run for this name.
              </div>
            ) : (
              <div className="space-y-2">
                {r.model.factors.map((f) => (
                  <div key={f.slug} className="flex items-start gap-2 text-sm">
                    <Badge variant="outline" className={`${FACTOR_STATUS_CLASS[f.status] || FACTOR_STATUS_CLASS.green} shrink-0`}>
                      {f.status?.toUpperCase()}
                    </Badge>
                    <div className="min-w-0">
                      <span className="font-medium">{f.name}</span>
                      <span className="text-muted-foreground"> · {f.magnitude} · {f.direction}</span>
                      {f.last_reading?.move_pct !== undefined && f.last_reading?.move_pct !== null ? (
                        <span className="text-xs text-muted-foreground"> · proxy {f.last_reading.symbol}: {pct(f.last_reading.move_pct)}</span>
                      ) : null}
                      {f.rationale ? <div className="text-xs text-muted-foreground">{f.rationale}</div> : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function EventsList({ portfolioId, kind, onChanged }) {
  const [items, setItems] = useState(null);
  const endpoint = kind === 'suggestions' ? 'suggestions' : 'events';

  const load = useCallback(() => {
    axios.get(`${API}/committee/portfolio/${portfolioId}/${endpoint}`)
      .then(({ data }) => setItems(data))
      .catch(() => setItems([]));
  }, [portfolioId, endpoint]);

  useEffect(() => { load(); }, [load]);

  const ack = async (id) => {
    try {
      await axios.post(`${API}/committee/portfolio/${portfolioId}/events/${id}/ack`);
      load();
      onChanged?.();
    } catch { /* noop */ }
  };

  if (items === null) return <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!items.length) {
    return (
      <div className="text-sm text-muted-foreground py-6 text-center">
        {kind === 'suggestions'
          ? 'No open suggestions. The daily pass surfaces verdict downgrades, new high-conviction candidates and redeploy ideas here.'
          : 'No alerts yet. Stops, targets, big moves, material news and factor trips land here.'}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {items.map((e) => (
        <div key={e.id} className={`flex items-start gap-3 p-3 rounded-lg border border-border/60 ${e.acknowledged ? 'opacity-50' : ''}`}>
          <Badge variant="outline" className={SEVERITY_CLASS[e.severity] || SEVERITY_CLASS.info}>
            {EVENT_LABEL[e.event_type] || e.event_type}
          </Badge>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">{e.title}</div>
            {e.detail_md ? <div className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">{e.detail_md}</div> : null}
            <div className="text-xs text-muted-foreground mt-1 flex gap-2">
              {e.nse_symbol ? <span className="font-medium">{e.nse_symbol}</span> : null}
              <span>{new Date(e.created_at).toLocaleString()}</span>
              {e.payload?.run_id ? (
                <Link className="underline" to={runHref(e.payload.run_id)}>view run</Link>
              ) : null}
            </div>
          </div>
          {!e.acknowledged ? (
            <Button size="sm" variant="ghost" onClick={() => ack(e.id)} title="Acknowledge">
              <Check className="w-4 h-4" />
            </Button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function PerformanceTab({ portfolioId, invested }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/committee/portfolio/${portfolioId}/performance`)
      .then(({ data }) => { if (alive) setRows(data); })
      .catch(() => { if (alive) setRows([]); });
    return () => { alive = false; };
  }, [portfolioId]);

  const data = useMemo(() => {
    if (!rows || !rows.length) return [];
    const baseBench = rows.find((r) => r.benchmark_close)?.benchmark_close;
    return rows.map((r) => ({
      date: r.as_of,
      portfolio: invested ? ((r.nav_inr - invested) / invested) * 100 : null,
      nifty50: baseBench && r.benchmark_close
        ? ((r.benchmark_close - baseBench) / baseBench) * 100 : null,
    }));
  }, [rows, invested]);

  if (rows === null) return <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!data.length) {
    return (
      <div className="text-sm text-muted-foreground py-6 text-center">
        NAV snapshots build up daily — the performance chart appears after the first snapshot.
      </div>
    );
  }
  return (
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 16, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
          <XAxis dataKey="date" tick={{ fontSize: 11 }} />
          <YAxis tickFormatter={(v) => `${v.toFixed(1)}%`} tick={{ fontSize: 11 }} />
          <ChartTooltip formatter={(v, k) => [`${Number(v).toFixed(2)}%`, k === 'portfolio' ? 'Portfolio' : 'Nifty 50']} />
          <Line type="monotone" dataKey="portfolio" stroke="#10b981" dot={false} strokeWidth={2} name="Portfolio" />
          <Line type="monotone" dataKey="nifty50" stroke="#6366f1" dot={false} strokeWidth={1.5} name="Nifty 50" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Page ─────────────────────────────────────────────────────

export default function CommitteePortfolioPage() {
  const [portfolios, setPortfolios] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [monitorBusy, setMonitorBusy] = useState(false);

  const loadPortfolios = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/committee/portfolio`);
      setPortfolios(data);
      if (data.length && !selectedId) setSelectedId(data[0].id);
    } catch {
      setPortfolios([]);
    }
  }, [selectedId]);

  const loadDetail = useCallback(async () => {
    if (!selectedId) return;
    try {
      const { data } = await axios.get(`${API}/committee/portfolio/${selectedId}`);
      setDetail(data);
    } catch {
      setDetail(null);
    }
  }, [selectedId]);

  useEffect(() => { loadPortfolios(); }, [loadPortfolios]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const runMonitors = useCallback(async () => {
    if (!selectedId) return;
    setMonitorBusy(true);
    try {
      await axios.post(`${API}/committee/portfolio/${selectedId}/monitor/run`);
      await loadDetail();
    } catch { /* surfaced via alerts tab */ } finally {
      setMonitorBusy(false);
    }
  }, [selectedId, loadDetail]);

  const onCreated = useCallback((pid) => {
    setShowWizard(false);
    setSelectedId(pid);
    loadPortfolios();
  }, [loadPortfolios]);

  const loading = portfolios === null;
  const empty = portfolios && portfolios.length === 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Committee Portfolio"
        subtitle="Tell the committee how much to invest — it builds, tracks and re-reviews a Nifty 50 portfolio for you"
        actions={(
          <div className="flex gap-2">
            {detail ? (
              <>
                <Button variant="outline" size="sm" onClick={runMonitors} disabled={monitorBusy}>
                  {monitorBusy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                  Run monitors now
                </Button>
                <Button variant="outline" size="sm" onClick={loadDetail}>
                  <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                </Button>
              </>
            ) : null}
            {!empty ? (
              <Button size="sm" onClick={() => setShowWizard((v) => !v)}>
                <Plus className="w-4 h-4 mr-1" /> New portfolio
              </Button>
            ) : null}
          </div>
        )}
      />

      {loading ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : null}

      {(empty || showWizard) && !loading ? <CreateWizard onCreated={onCreated} /> : null}

      {portfolios && portfolios.length > 1 ? (
        <div className="flex flex-wrap gap-2">
          {portfolios.map((p) => (
            <Button
              key={p.id}
              size="sm"
              variant={p.id === selectedId ? 'default' : 'outline'}
              onClick={() => { setSelectedId(p.id); setDetail(null); }}
            >
              {p.name} {p.open_events ? <Badge variant="outline" className="ml-2">{p.open_events}</Badge> : null}
            </Button>
          ))}
        </div>
      ) : null}

      {detail ? (
        <div className="space-y-4">
          <SummaryCards detail={detail} />
          <Tabs defaultValue="holdings">
            <TabsList>
              <TabsTrigger value="holdings"><Briefcase className="w-3.5 h-3.5 mr-1" /> Holdings</TabsTrigger>
              <TabsTrigger value="news"><Newspaper className="w-3.5 h-3.5 mr-1" /> News</TabsTrigger>
              <TabsTrigger value="factors"><Radar className="w-3.5 h-3.5 mr-1" /> Factors</TabsTrigger>
              <TabsTrigger value="alerts"><AlertTriangle className="w-3.5 h-3.5 mr-1" /> Alerts</TabsTrigger>
              <TabsTrigger value="suggestions"><Lightbulb className="w-3.5 h-3.5 mr-1" /> Suggestions</TabsTrigger>
              <TabsTrigger value="performance"><Activity className="w-3.5 h-3.5 mr-1" /> Performance</TabsTrigger>
            </TabsList>
            <TabsContent value="holdings" className="mt-4"><HoldingsTab detail={detail} /></TabsContent>
            <TabsContent value="news" className="mt-4"><NewsTab portfolioId={detail.id} holdings={detail.holdings} /></TabsContent>
            <TabsContent value="factors" className="mt-4"><FactorsTab portfolioId={detail.id} /></TabsContent>
            <TabsContent value="alerts" className="mt-4">
              <EventsList portfolioId={detail.id} kind="events" onChanged={loadPortfolios} />
            </TabsContent>
            <TabsContent value="suggestions" className="mt-4">
              <EventsList portfolioId={detail.id} kind="suggestions" onChanged={loadPortfolios} />
            </TabsContent>
            <TabsContent value="performance" className="mt-4">
              <PerformanceTab portfolioId={detail.id} invested={detail.invested_amount_inr} />
            </TabsContent>
          </Tabs>
        </div>
      ) : null}
    </div>
  );
}
